import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { ComponentProps } from 'src/types/component-props';
import { CoreEmailDisclaimerData } from 'src/types/components';

import CloseIcon from 'src/assets/icons/close.svg';

import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import styles from './CoreEmailDisclaimer.module.scss';

export type CoreEmailDisclaimerProps = ComponentProps & CoreEmailDisclaimerData;

const CoreEmailDisclaimer = (props: CoreEmailDisclaimerProps): JSX.Element => {
  const { title, descriptionWithTokens, descriptionWithOutTokens, ctaLabel } = props.fields;

  const [isOpen, setIsOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const formattedDescription =
    firstName || lastName
      ? descriptionWithTokens
          .replaceAll(/\[Firstname Lastname\]/gi, [firstName, lastName].filter(Boolean).join(' '))
          .replaceAll(/\[Firstname\]/gi, `${firstName}`)
          .replaceAll(/\[Lastname\]/gi, `${lastName}`)
      : descriptionWithOutTokens;

  const triggerElement = useRef<HTMLAnchorElement | null>(null);

  const hasContent = title && formattedDescription && ctaLabel;

  useEffect(() => {
    if (!hasContent) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (event.target && event.target instanceof Element) {
        // check to see if target or an ancestor of the target is an email link
        const emailLink = event.target.closest<HTMLAnchorElement>('a[href^="mailto:"]');

        // ensure that the email is a @dickinsonwright.com email before triggering disclaimer
        if (emailLink && emailLink?.href.match(/@dickinson\-?wright\.com$|@dwlaw\.com$/i)) {
          event.preventDefault();
          triggerElement.current = emailLink;
          setEmailAddress(emailLink.href);
          setFirstName(emailLink.dataset.firstname || '');
          setLastName(emailLink.dataset.lastname || '');
          setIsOpen(true);
        }
      }
    };
    document.body.addEventListener('click', handleClick);
    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, [setEmailAddress, setIsOpen]);

  const acceptDisclaimer = () => {
    window.location.href = emailAddress;
    setIsOpen(false);
  };

  if (!hasContent) {
    return <></>;
  }

  return (
    <div className={styles.main}>
      <Dialog.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <Portal>
          <Dialog.Backdrop className={styles.backdrop} />
          <Dialog.Positioner className={styles.positioner}>
            <Dialog.Content className={cn(styles.content)}>
              <div className={styles['content-wrapper']}>
                <Dialog.CloseTrigger className={styles.close}>
                  <CloseIcon className={styles['close-icon']} />
                </Dialog.CloseTrigger>
                <div>
                  <Dialog.Title className={cn(styles.title, 'heading-h4')}>
                    <Text field={title} />
                  </Dialog.Title>
                  <Dialog.Description>
                    <RichText
                      className={cn(styles.description, 'body-med')}
                      field={formattedDescription}
                    />
                  </Dialog.Description>
                </div>
                <Cta
                  tag="button"
                  className={styles.cta}
                  onClick={acceptDisclaimer}
                  showIcon={false}
                >
                  {ctaLabel}
                </Cta>
              </div>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </div>
  );
};

export default CoreEmailDisclaimer;
